import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    IonTitle,
    IonToast,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";

import {
    checkmarkOutline,
    closeOutline,
    lockClosedOutline,
    refreshOutline,
    returnDownBackOutline,
    swapHorizontalOutline
} from "ionicons/icons";

import {
    useCallback,
    useEffect,
    useState
} from "react";

import { useParams } from "react-router-dom";
import Cookies from "js-cookie";

import "./FantasyCommissionerTrades.css";

const apiBaseUrl =
    "https://fantasy.ftblscore.com/create_league";

type TradeAction =
    | "accept"
    | "cancel"
    | "revert";

interface TradePlayer {
    id: string;
    name: string;
    position: string;
    teamName: string;
    isLocked: boolean;
}

interface CommissionerTrade {
    id: string;
    status: string;
    message: string;
    createdAt: string;
    resolvedAt: string;
    fromTeamId: string;
    fromTeamName: string;
    toTeamId: string;
    toTeamName: string;
    offeredPlayer: TradePlayer;
    requestedPlayer: TradePlayer;
    isLocked: boolean;
}

interface CommissionerTradesState {
    success: boolean;
    message?: string;
    league: {
        id: string;
        name: string;
    };
    week: {
        realRound: number;
        fantasyRound: number;
    };
    trades: CommissionerTrade[];
}

function getTradeStatusLabel(status: string): string {
    const labels: Record<string, string> = {
        pending: "Pendiente",
        accepted: "Aceptado",
        cancelled: "Cancelado",
        invalid: "Inválido",
        expired: "Vencido",
        reverted: "Revertido"
    };

    return labels[status] || "Sin estado";
}

function formatDate(value: string): string {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(
        "es-MX",
        {
            dateStyle: "medium",
            timeStyle: "short"
        }
    ).format(date);
}

const FantasyCommissionerTrades: React.FC = () => {
    const router = useIonRouter();
    const { id } = useParams<{ id: string }>();

    const [state, setState] =
        useState<CommissionerTradesState | null>(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [updatingTradeId, setUpdatingTradeId] =
        useState("");

    const loadTrades = useCallback(
        async function (forceRefresh = false) {
            const username =
                Cookies.get("access_username") || "";

            const token =
                Cookies.get("access_token") || "";

            if (!username || !token) {
                router.push(
                    "/home",
                    "root",
                    "replace"
                );

                return;
            }

            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const response = await fetch(
                    `${apiBaseUrl}/control_CommissionerFantasyTrades.php` +
                        `?id=${encodeURIComponent(id)}` +
                        `&t=${Date.now()}`,
                    {
                        credentials: "include",
                        cache: "no-store",
                        headers: {
                            Accept: "application/json",
                            "X-Access-Username": username,
                            "X-Access-Token": token
                        }
                    }
                );

                const result =
                    await response.json() as CommissionerTradesState;

                if (!response.ok || !result.success) {
                    throw new Error(
                        result.message ||
                        "No fue posible cargar los intercambios."
                    );
                }

                setState(result);
            } catch (requestError) {
                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar los intercambios."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id, router]
    );

    useEffect(
        function () {
            loadTrades();
        },
        [loadTrades]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ): Promise<void> {
        await loadTrades(true);
        event.detail.complete();
    }

    async function executeAction(
        trade: CommissionerTrade,
        action: TradeAction
    ): Promise<void> {
        if (updatingTradeId) {
            return;
        }

        const actionLabel: Record<TradeAction, string> = {
            accept: "aceptar",
            cancel: "cancelar",
            revert: "revertir"
        };

        const confirmed = window.confirm(
            `¿Deseas ${actionLabel[action]} este intercambio?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setUpdatingTradeId(trade.id);

            const response = await fetch(
                `${apiBaseUrl}/control_CommissionerFantasyTrades.php`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        "X-Access-Username":
                            Cookies.get(
                                "access_username"
                            ) || "",
                        "X-Access-Token":
                            Cookies.get(
                                "access_token"
                            ) || ""
                    },
                    body: JSON.stringify({
                        leagueId: id,
                        tradeId: trade.id,
                        action
                    })
                }
            );

            const result = await response.json() as {
                success?: boolean;
                message?: string;
            };

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                    "No fue posible actualizar el intercambio."
                );
            }

            setMessage(
                result.message ||
                "Intercambio actualizado correctamente."
            );

            await loadTrades(true);
        } catch (requestError) {
            setMessage(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible actualizar el intercambio."
            );
        } finally {
            setUpdatingTradeId("");
        }
    }

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="commissioner-trades-toolbar">
                    <IonButtons slot="start">
                        <IonBackButton
                            defaultHref={`/fantasySettings/${id}`}
                            text=""
                        />
                    </IonButtons>

                    <IonTitle>
                        Intercambios
                    </IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="commissioner-trades-content"
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando intercambios..."
                    />
                </IonRefresher>

                {loading && (
                    <IonProgressBar type="indeterminate" />
                )}

                {!loading && error && (
                    <main className="commissioner-trades-main">
                        <div className="commissioner-trades-empty">
                            <IonIcon icon={refreshOutline} />
                            <strong>
                                No pudimos cargar los intercambios
                            </strong>
                            <span>{error}</span>

                            <IonButton
                                size="small"
                                fill="outline"
                                onClick={function () {
                                    loadTrades(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </main>
                )}

                {!loading && !error && state && (
                    <>
                        <section className="commissioner-trades-hero">
                            <IonIcon icon={swapHorizontalOutline} />

                            <div>
                                <span>
                                    {state.league.name}
                                </span>

                                <h1>
                                    Control de intercambios
                                </h1>

                                <p>
                                    Jornada actual:{" "}
                                    {state.week.realRound || "—"}
                                </p>
                            </div>
                        </section>

                        <main className="commissioner-trades-main">
                            {refreshing && (
                                <div className="commissioner-trades-refreshing">
                                    <IonSpinner name="crescent" />
                                    Actualizando
                                </div>
                            )}

                            {state.trades.length === 0 ? (
                                <div className="commissioner-trades-empty">
                                    <IonIcon
                                        icon={swapHorizontalOutline}
                                    />

                                    <strong>
                                        No hay intercambios registrados
                                    </strong>

                                    <span>
                                        Las propuestas y movimientos
                                        aparecerán aquí.
                                    </span>
                                </div>
                            ) : (
                                <div className="commissioner-trades-list">
                                    {state.trades.map(
                                        function (trade) {
                                            const isUpdating =
                                                updatingTradeId ===
                                                trade.id;

                                            const canAccept =
                                                trade.status ===
                                                    "pending" &&
                                                !trade.isLocked &&
                                                !isUpdating;

                                            const canCancel =
                                                trade.status ===
                                                    "pending" &&
                                                !isUpdating;

                                            const canRevert =
                                                trade.status ===
                                                    "accepted" &&
                                                !trade.isLocked &&
                                                !isUpdating;

                                            return (
                                                <article
                                                    key={trade.id}
                                                    className="commissioner-trade-card"
                                                >
                                                    <div className="commissioner-trade-card__top">
                                                        <span
                                                            className={
                                                                `commissioner-trade-card__status ` +
                                                                `commissioner-trade-card__status--${trade.status}`
                                                            }
                                                        >
                                                            {getTradeStatusLabel(
                                                                trade.status
                                                            )}
                                                        </span>

                                                        <small>
                                                            {formatDate(
                                                                trade.createdAt
                                                            )}
                                                        </small>
                                                    </div>

                                                    <div className="commissioner-trade-card__teams">
                                                        <strong>
                                                            {trade.fromTeamName}
                                                        </strong>

                                                        <IonIcon
                                                            icon={
                                                                swapHorizontalOutline
                                                            }
                                                        />

                                                        <strong>
                                                            {trade.toTeamName}
                                                        </strong>
                                                    </div>

                                                    <div className="commissioner-trade-card__players">
                                                        <div>
                                                            <span>
                                                                ENVÍA
                                                            </span>

                                                            <strong>
                                                                {trade.offeredPlayer.name}
                                                            </strong>

                                                            <small>
                                                                {trade.offeredPlayer.position} ·{" "}
                                                                {trade.offeredPlayer.teamName}
                                                            </small>
                                                        </div>

                                                        <IonIcon
                                                            icon={
                                                                swapHorizontalOutline
                                                            }
                                                        />

                                                        <div>
                                                            <span>
                                                                RECIBE
                                                            </span>

                                                            <strong>
                                                                {trade.requestedPlayer.name}
                                                            </strong>

                                                            <small>
                                                                {trade.requestedPlayer.position} ·{" "}
                                                                {trade.requestedPlayer.teamName}
                                                            </small>
                                                        </div>
                                                    </div>

                                                    {trade.isLocked && (
                                                        <div className="commissioner-trade-card__locked">
                                                            <IonIcon
                                                                icon={
                                                                    lockClosedOutline
                                                                }
                                                            />

                                                            No se puede aceptar ni
                                                            revertir: al menos un
                                                            jugador está bloqueado.
                                                        </div>
                                                    )}

                                                    {trade.message && (
                                                        <p className="commissioner-trade-card__message">
                                                            {trade.message}
                                                        </p>
                                                    )}

                                                    {(canAccept ||
                                                        canCancel ||
                                                        canRevert) && (
                                                        <div className="commissioner-trade-card__actions">
                                                            {trade.status ===
                                                                "pending" && (
                                                                <>
                                                                    <IonButton
                                                                        size="small"
                                                                        color="success"
                                                                        disabled={
                                                                            !canAccept
                                                                        }
                                                                        onClick={function () {
                                                                            executeAction(
                                                                                trade,
                                                                                "accept"
                                                                            );
                                                                        }}
                                                                    >
                                                                        <IonIcon
                                                                            slot="start"
                                                                            icon={
                                                                                checkmarkOutline
                                                                            }
                                                                        />

                                                                        Aceptar
                                                                    </IonButton>

                                                                    <IonButton
                                                                        size="small"
                                                                        fill="outline"
                                                                        color="medium"
                                                                        disabled={
                                                                            !canCancel
                                                                        }
                                                                        onClick={function () {
                                                                            executeAction(
                                                                                trade,
                                                                                "cancel"
                                                                            );
                                                                        }}
                                                                    >
                                                                        <IonIcon
                                                                            slot="start"
                                                                            icon={
                                                                                closeOutline
                                                                            }
                                                                        />

                                                                        Cancelar
                                                                    </IonButton>
                                                                </>
                                                            )}

                                                            {trade.status ===
                                                                "accepted" && (
                                                                <IonButton
                                                                    size="small"
                                                                    fill="outline"
                                                                    color="warning"
                                                                    disabled={
                                                                        !canRevert
                                                                    }
                                                                    onClick={function () {
                                                                        executeAction(
                                                                            trade,
                                                                            "revert"
                                                                        );
                                                                    }}
                                                                >
                                                                    <IonIcon
                                                                        slot="start"
                                                                        icon={
                                                                            returnDownBackOutline
                                                                        }
                                                                    />

                                                                    Revertir
                                                                </IonButton>
                                                            )}
                                                        </div>
                                                    )}
                                                </article>
                                            );
                                        }
                                    )}
                                </div>
                            )}
                        </main>
                    </>
                )}
            </IonContent>

            <IonToast
                isOpen={Boolean(message)}
                message={message}
                duration={3200}
                position="bottom"
                onDidDismiss={function () {
                    setMessage("");
                }}
            />
        </IonPage>
    );
};

export default FantasyCommissionerTrades;
